import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { Spinner } from '../../../shared/components';
import {
  createTikTokAppChannelAccount,
  getTikTokAppSession,
  getTikTokAppStatus,
} from '../services/channelAccountService';
import { TikTokAppSession } from '../types/channelAccount';
import { useChannelAccounts } from '../hooks/useChannelAccounts';
import { ChannelAccountBar } from './ChannelAccountBar';

interface TikTokAppConnectorTabProps {
  canManage: boolean;
}

const POLL_MS = 5000;

const formatStamp = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

export const TikTokAppConnectorTab: React.FC<TikTokAppConnectorTabProps> = ({ canManage }) => {
  const { t } = useTranslation(['companies', 'admin']);
  const { showToast } = useToast();
  const accounts = useChannelAccounts('TikTok', canManage, 'app');

  const [platformConfigured, setPlatformConfigured] = React.useState<boolean | null>(null);
  const [recommendedSku, setRecommendedSku] = React.useState<string>('real-phone');
  const [monthlyPriceUsd, setMonthlyPriceUsd] = React.useState<number>(39);
  const [session, setSession] = React.useState<TikTokAppSession | null>(null);
  const [loadingSession, setLoadingSession] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [displayName, setDisplayName] = React.useState('');
  const [padCode, setPadCode] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [showAddForm, setShowAddForm] = React.useState(false);

  React.useEffect(() => {
    if (!canManage) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const status = await getTikTokAppStatus();
        if (!cancelled) {
          setPlatformConfigured(status.configured);
          if (status.recommendedSku) {
            setRecommendedSku(status.recommendedSku);
          }
          if (typeof status.monthlyPriceUsd === 'number') {
            setMonthlyPriceUsd(status.monthlyPriceUsd);
          }
        }
      } catch {
        if (!cancelled) {
          setPlatformConfigured(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canManage]);

  const loadSession = React.useCallback(async (accountId: string, options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoadingSession(true);
    }
    setError(null);
    try {
      const next = await getTikTokAppSession(accountId);
      setSession(next);
      return next;
    } catch (loadError) {
      if (!options?.silent) {
        setSession(null);
        setError(loadError instanceof Error ? loadError.message : t('failedToLoadTikTokAppSession', { ns: 'companies' }));
      }
      return null;
    } finally {
      if (!options?.silent) {
        setLoadingSession(false);
      }
    }
  }, [t]);

  React.useEffect(() => {
    const accountId = accounts.selectedAccountId;
    if (!accountId) {
      setSession(null);
      return;
    }

    void loadSession(accountId);
  }, [accounts.selectedAccountId, loadSession]);

  React.useEffect(() => {
    const accountId = accounts.selectedAccountId;
    const status = session?.connectionStatus?.toLowerCase();
    const shouldPoll = Boolean(accountId && status && status !== 'close');
    if (!shouldPoll || !accountId) {
      return;
    }

    const timer = window.setInterval(() => {
      void loadSession(accountId, { silent: true });
    }, POLL_MS);

    return () => window.clearInterval(timer);
  }, [accounts.selectedAccountId, loadSession, session?.connectionStatus]);

  const createAccount = async () => {
    const name = displayName.trim();
    const pad = padCode.trim();
    if (!name) {
      const message = t('tiktokAppDisplayNameRequired', { ns: 'companies' });
      setError(message);
      showToast(message, 'error');
      return;
    }

    if (!pad) {
      const message = t('tiktokAppPadCodeRequired', { ns: 'companies' });
      setError(message);
      showToast(message, 'error');
      return;
    }

    setCreating(true);
    setError(null);
    setNotice(null);

    try {
      const created = await createTikTokAppChannelAccount({
        displayName: name,
        padCode: pad,
        isDefault: accounts.accounts.length === 0,
      });
      setSession(created);
      setDisplayName('');
      setPadCode('');
      setShowAddForm(false);
      await accounts.reload();
      accounts.setSelectedAccountId(created.account.id);
      const message = t('tiktokAppAccountCreated', { ns: 'companies' });
      setNotice(message);
      showToast(message, 'success');
    } catch (createError) {
      const message = createError instanceof Error ? createError.message : t('failedToCreateTikTokAppAccount', { ns: 'companies' });
      setError(message);
      showToast(message, 'error');
    } finally {
      setCreating(false);
    }
  };

  if (!canManage) {
    return null;
  }

  const connected = session?.connectionStatus?.toLowerCase() === 'open';
  const statusLabel = t(`tiktokAppStatus.${session?.connectionStatus ?? 'connecting'}`, {
    ns: 'companies',
    defaultValue: session?.connectionStatus ?? t('tiktokAppStatus.connecting', { ns: 'companies' }),
  });

  return (
    <section className="company-section" style={{ marginTop: 8 }}>
      <ChannelAccountBar
        channel="TikTok"
        connectionKind="app"
        accounts={accounts.accounts}
        limits={accounts.limits}
        loading={accounts.loading}
        error={accounts.error}
        selectedAccountId={accounts.selectedAccountId}
        onSelectAccount={accounts.setSelectedAccountId}
        onRefresh={() => { void accounts.reload(); }}
        canManage={canManage}
        showAddForm={showAddForm}
        onToggleAddForm={() => setShowAddForm(current => !current)}
        onAccountUpdated={() => { void accounts.reload(); }}
      />

      {showAddForm ? (
        <div className="company-card" style={{ marginTop: 16 }}>
          <h4>{t('tiktokAppAddAccountHeading', { ns: 'companies' })}</h4>
          <label>
            {t('tiktokAppDisplayName', { ns: 'companies' })}
            <input
              className="text-input"
              value={displayName}
              onChange={event => setDisplayName(event.target.value)}
              disabled={creating}
            />
          </label>
          <label>
            {t('tiktokAppPadCode', { ns: 'companies' })}
            <input
              className="text-input"
              value={padCode}
              onChange={event => setPadCode(event.target.value)}
              disabled={creating}
            />
          </label>
          <div className="company-actions-inline" style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-primary" onClick={() => { void createAccount(); }} disabled={creating}>
              {creating ? t('savingEllipsis', { ns: 'admin' }) : t('tiktokAppCreateAccount', { ns: 'companies' })}
            </button>
          </div>
        </div>
      ) : null}

      {platformConfigured === false ? (
        <div className="company-card" style={{ marginTop: 16 }}>
          <p>{t('tiktokAppNotConfigured', { ns: 'companies' })}</p>
        </div>
      ) : null}

      {error ? <div className="error" style={{ marginTop: 12 }}>{error}</div> : null}
      {notice ? <div className="success" style={{ marginTop: 12 }}>{notice}</div> : null}

      {loadingSession ? <Spinner text={t('loadingTikTokAppSession', { ns: 'companies' })} /> : null}

      {session ? (
        <div className="company-card" style={{ marginTop: 16 }}>
          <h4>{t('tiktokAppSessionHeading', { ns: 'companies' })}</h4>
          <p>{t('tiktokAppInstructions', { ns: 'companies' })}</p>
          <p>{t('tiktokAppWarning', { ns: 'companies' })}</p>
          <p>{t('tiktokAppSkuNote', { ns: 'companies', sku: recommendedSku, price: monthlyPriceUsd })}</p>
          <dl>
            <dt>{t('tiktokAppConnectionStatus', { ns: 'companies' })}</dt>
            <dd>{connected ? t('tiktokAppConnected', { ns: 'companies' }) : statusLabel}</dd>
            <dt>{t('tiktokAppPadCode', { ns: 'companies' })}</dt>
            <dd><code>{session.padCode}</code></dd>
            <dt>{t('tiktokAppIngestUrl', { ns: 'companies' })}</dt>
            <dd><code>{session.ingestUrl}</code></dd>
            <dt>{t('tiktokAppDeviceSecret', { ns: 'companies' })}</dt>
            <dd><code>{session.deviceSecret}</code></dd>
            <dt>{t('tiktokAppLastHeartbeat', { ns: 'companies' })}</dt>
            <dd>{formatStamp(session.lastHeartbeatAtUtc)}</dd>
            <dt>{t('tiktokAppLastEvent', { ns: 'companies' })}</dt>
            <dd>{formatStamp(session.lastEventAtUtc)}</dd>
            <dt>{t('tiktokAppWatcherSource', { ns: 'companies' })}</dt>
            <dd>{session.watcherSource || '—'}</dd>
          </dl>
        </div>
      ) : null}
    </section>
  );
};
